import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { Option } from '../../../../shared/models/option.model';
import { NotificationService } from '../../../../modules_v2/shared/services/notification.service';
import { Model184Service } from '../../model-184.service';
import { PROVINCIAS_AEAT, SINO_AEAT } from '../../tax-catalogs';
import {
  CLAVE_DECLARADO_184,
  Model184Miembro,
  Model184Validation,
  NATURALEZA_INMUEBLE_184,
  SITUACION_INMUEBLE_184,
  TIPO_PARTICIPE_184,
  normalizarMiembro184,
  nuevoMiembro184
} from '../taxes-184.model';

export interface Model184MiembroDialogData {
  miembro: Model184Miembro | null;
  ejercicio: number;
  validaciones: Model184Validation[];
}

export interface Model184MiembroDialogResult {
  miembro: Model184Miembro;
}

@Component({
  selector: 'app-model-184-miembro-dialog',
  templateUrl: './model-184-miembro-dialog.component.html',
  styleUrls: ['./model-184-miembro-dialog.component.css'],
  standalone: false
})
export class Model184MiembroDialogComponent {

  f: any;
  saving = false;
  validaciones: Model184Validation[];

  readonly tipoParticipeOptions: Option[] = TIPO_PARTICIPE_184;
  readonly provinciaOptions: Option[] = PROVINCIAS_AEAT;
  readonly sinoOptions: Option[] = SINO_AEAT;
  readonly naturalezaOptions: Option[] = NATURALEZA_INMUEBLE_184;
  readonly situacionOptions: Option[] = SITUACION_INMUEBLE_184;
  readonly claveDeclaradoOptions: Option[] = CLAVE_DECLARADO_184;

  static open(dialog: MatDialog, data: Model184MiembroDialogData): Observable<Model184MiembroDialogResult | null> {
    return dialog.open<Model184MiembroDialogComponent, Model184MiembroDialogData, Model184MiembroDialogResult>(
      Model184MiembroDialogComponent, {
      width: '980px',
      maxWidth: '96vw',
      maxHeight: '92vh',
      autoFocus: false,
      data
    }).afterClosed().pipe(map(resultado => resultado || null));
  }

  constructor(
    private dialogRef: MatDialogRef<Model184MiembroDialogComponent, Model184MiembroDialogResult>,
    @Inject(MAT_DIALOG_DATA) public data: Model184MiembroDialogData,
    private service: Model184Service,
    private notification: NotificationService
  ) {
    const origen = data.miembro ? normalizarMiembro184(data.miembro) : nuevoMiembro184();

    this.f = {
      ...origen,
      miembroA31SN: origen.miembroA31 ? 'S' : 'N',
      porcentajeTitularidad: origen.porcentajeTitularidad ?? ''
    };

    this.validaciones = data.validaciones || [];
  }

  get titulo(): string {
    return this.data.miembro ? 'Editar miembro' : 'Nuevo miembro';
  }

  get subtitulo(): string {
    return `Modelo 184 · ejercicio ${this.data.ejercicio}`;
  }

  mensaje(campo: string): Model184Validation | null {
    return this.validaciones.find(item => item.key === campo) || null;
  }

  get sueltas(): Model184Validation[] {
    return this.validaciones.filter(item => !item.key);
  }

  set(campo: string, valor: any) {
    this.f[campo] = valor;
    this.validaciones = this.validaciones.filter(item => item.key !== campo);
  }

  trackByIndex(i: number) { return i; }

  private numero(valor: any): number {
    const convertido = Number(String(valor ?? '').replace(',', '.'));

    return isNaN(convertido) ? 0 : convertido;
  }

  private aDto(): Model184Miembro {
    const f = this.f;

    return normalizarMiembro184({
      ...f,
      origen: 'MANUAL',
      miembroA31: f.miembroA31SN === 'S',
      dias: this.numero(f.dias),
      participacion: this.numero(f.participacion),
      diasArrendamiento: this.numero(f.diasArrendamiento),
      porcentajeTitularidad: f.porcentajeTitularidad === '' || f.porcentajeTitularidad === null
        ? null : this.numero(f.porcentajeTitularidad)
    });
  }

  guardar() {
    if (this.saving) return;

    const miembro = this.aDto();

    this.saving = true;
    this.dialogRef.disableClose = true;

    this.service.saveMember(this.data.ejercicio, miembro)
      .then(guardado => {
        this.saving = false;
        this.dialogRef.disableClose = false;
        this.dialogRef.close({ miembro: guardado });
      })
      .catch(err => {
        this.saving = false;
        this.dialogRef.disableClose = false;

        const mensajes: string[] = err?.error?.error?.messages;

        this.notification.error(mensajes?.length ? mensajes.join('. ') : 'No se ha podido guardar el miembro');
      });
  }

  cerrar() {
    if (this.saving) return;

    this.dialogRef.close(undefined);
  }
}
